import { createFileRoute, Link } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import hero640Asset from "../assets/hero-640.webp.asset.json";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";

const heroSrcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;

export const Route = createFileRoute("/entorno")({
  head: () => ({
    meta: [
      { title: "Qué ver en la Sierra del Alto Rey · Guadalajara" },
      { name: "description", content: "Guía del entorno: Pico Alto Rey, Hayedo de Tejera Negra, pueblos negros (Majaelrayo, Valverde, Campillo) y cielos estrellados en la Sierra de Guadalajara." },
      { property: "og:title", content: "Qué ver en la Sierra del Alto Rey · Guadalajara" },
      { property: "og:description", content: "Qué ver y hacer alrededor de El Ordial: senderismo, pueblos negros, arquitectura serrana y gastronomía." },
      { property: "og:url", content: "/entorno" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/entorno" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Entorno", to: "/entorno" },
          ]),
        ),
      },
    ],
  }),
  component: Entorno,
});

type Plan = { t: string; d: string; km?: string };
type Categoria = { titulo: string; intro: string; planes: Plan[] };

const categorias: Categoria[] = [
  {
    titulo: "Naturaleza y montaña",
    intro: "El Ordial está al pie del Pico Alto Rey, un mirador natural sobre el Sistema Central y la Alcarria.",
    planes: [
      { t: "Pico y ermita del Alto Rey", d: "Ascensión a la cumbre (1.852 m) coronada por la ermita románica. Vistas de 360° sobre la Sierra Norte y, en días claros, hasta Gredos y Guadarrama.", km: "A pie desde el pueblo" },
      { t: "Chorreras de Despeñalagua", d: "Cascada de más de 80 m de caída junto a Valverde de los Arroyos. Una de las imágenes más icónicas de la sierra.", km: "≈ 25 min en coche" },
      { t: "Hayedo de Tejera Negra", d: "Parque Natural y Reserva de la Biosfera. Uno de los hayedos más meridionales de Europa, espectacular en otoño (acceso regulado).", km: "≈ 50 min" },
      { t: "Cañón del Río Dulce", d: "Paisaje protegido entre Pelegrina y Aragosa, hogar de buitres leonados y rapaces. Mirador de Félix Rodríguez de la Fuente.", km: "≈ 1 h" },
      { t: "Embalses de Pálmaces y Alcorlo", d: "Láminas de agua entre robledales y encinares, ideales para baño, pesca o un picnic tranquilo.", km: "20–40 min" },
    ],
  },
  {
    titulo: "Pueblos de Arquitectura Negra",
    intro: "Casas de pizarra integradas en el paisaje, declaradas Conjunto Histórico. Todos a menos de 30 minutos.",
    planes: [
      { t: "Valverde de los Arroyos", d: "Uno de los Pueblos Más Bonitos de España. Plaza con olmo centenario y arquitectura de lascas perfectamente conservada.", km: "≈ 20 min" },
      { t: "Majaelrayo", d: "Capital de la arquitectura negra, a los pies del Ocejón. Recorrido a pie por callejones empedrados.", km: "≈ 30 min" },
      { t: "Campillo de Ranas y sus pedanías", d: "Roblelacasa, El Espinar y Robleluengo, pequeños núcleos negros muy bien rehabilitados.", km: "≈ 25 min" },
      { t: "Umbralejo", d: "Pueblo-escuela recuperado por el Programa de Pueblos Abandonados. Visita guiada bajo reserva.", km: "≈ 15 min" },
      { t: "Almiruete", d: "Famoso por sus botargas y mascaritas de Carnaval, declaradas de Interés Turístico.", km: "≈ 20 min" },
    ],
  },
  {
    titulo: "Pueblos rojos y patrimonio cercano",
    intro: "Más allá de la pizarra, la sierra esconde rincones de areniscas rojizas y villas medievales.",
    planes: [
      { t: "Ciudad Encantada de Tamajón", d: "Formaciones calizas modeladas por la erosión, paseo corto y muy fotogénico.", km: "≈ 20 min" },
      { t: "Pueblos rojos: Palancares y Zarzuela de Jadraque", d: "Casas de adobe y arenisca roja en plena dehesa.", km: "≈ 15 min" },
      { t: "Hiendelaencina", d: "Antiguas minas de plata del siglo XIX y su museo minero.", km: "≈ 20 min" },
      { t: "Cogolludo", d: "Plaza Mayor presidida por el Palacio de los Duques de Medinaceli, primer palacio renacentista de España.", km: "≈ 30 min" },
    ],
  },
  {
    titulo: "Villas medievales para una escapada de día",
    intro: "Si te apetece una excursión más larga, dos joyas de la provincia.",
    planes: [
      { t: "Atienza", d: "Villa medieval coronada por su castillo roquero, con murallas, museos y la tradición de La Caballada.", km: "≈ 45 min" },
      { t: "Sigüenza", d: "Catedral, castillo-parador y casco histórico declarado Conjunto Monumental.", km: "≈ 1 h 15" },
    ],
  },
  {
    titulo: "Rutas, senderismo y BTT",
    intro: "Decenas de kilómetros señalizados parten del propio Ordial o de pueblos cercanos.",
    planes: [
      { t: "Sendero del Alto Rey (PR-GU)", d: "Ruta clásica circular, apta para toda la familia en su versión corta." },
      { t: "Senda de los Pueblos Negros", d: "Conecta a pie Majaelrayo, Campillo y Valverde por antiguos caminos arrieros." },
      { t: "Rutas BTT por la Sierra Norte", d: "Pistas forestales entre robledales, dehesas y riberas, con desniveles para todos los niveles." },
      { t: "Rutas a caballo y orientación", d: "Empresas locales organizan jornadas de aventura, geocaching y multiaventura familiar." },
    ],
  },
  {
    titulo: "Cielos estrellados",
    intro: "El entorno de El Ordial es una de las zonas con menor contaminación lumínica del centro peninsular.",
    planes: [
      { t: "Observación de estrellas", d: "Desde la propia parcela del Pajar disfrutarás de la Vía Láctea las noches sin luna." },
      { t: "Lluvias de meteoros", d: "Perseidas (agosto) y Gemínidas (diciembre) se viven aquí con un cielo de calidad excepcional." },
    ],
  },
  {
    titulo: "Gastronomía y producto local",
    intro: "Sabores serranos auténticos en obradores, queserías y restaurantes a pocos kilómetros.",
    planes: [
      { t: "Cabrito y cordero asado", d: "Especialidad de la sierra, en hornos de leña tradicionales de los pueblos cercanos." },
      { t: "Migas, judiones y setas", d: "Cocina de cuchara con producto de temporada, recogida micológica en otoño." },
      { t: "Queserías artesanas", d: "Quesos de cabra y oveja elaborados en la comarca, ideales para llevarse a casa." },
      { t: "Miel de la Alcarria (DOP)", d: "Visitas a colmenares y obradores cercanos para conocer una de las grandes mieles de España." },
    ],
  },
  {
    titulo: "Patrimonio en el pueblo",
    intro: "Sin salir de El Ordial, pequeños tesoros para una tarde de paseo.",
    planes: [
      { t: "Iglesia de San Pedro Apóstol", d: "Templo serrano de piedra y pizarra que preside el casco urbano." },
      { t: "Ermita de la Soledad", d: "Pequeña ermita en las afueras, escenario de las romerías locales." },
      { t: "Fuentes y lavaderos", d: "Conjuntos de piedra restaurados que recuerdan la vida tradicional del pueblo." },
    ],
  },
];

function Entorno() {
  const { config } = useSiteData();
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Entorno" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={heroSrcSet} sizes="100vw" alt="Sierra del Alto Rey al atardecer" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">El entorno</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Sierra del Alto Rey</h1>
          <p className="mt-4 max-w-2xl text-white/90">{config.entorno_intro}</p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-20 md:px-6">
        <div className="space-y-20">
          {categorias.map((cat) => (
            <div key={cat.titulo}>
              <div className="mb-8 max-w-2xl">
                <h2 className="font-serif text-2xl text-foreground md:text-3xl">{cat.titulo}</h2>
                <p className="mt-3 text-muted-foreground">{cat.intro}</p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {cat.planes.map((p) => (
                  <article key={p.t} className="flex flex-col rounded-sm border border-border bg-card p-6 transition-shadow hover:shadow-lg">
                    <h3 className="font-serif text-lg text-foreground">{p.t}</h3>
                    <p className="mt-3 flex-1 text-sm text-muted-foreground">{p.d}</p>
                    {p.km && <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground/80">{p.km}</p>}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">¿Quieres ideas para tu escapada?</h2>
        <p className="mt-4 text-muted-foreground">Te preparamos sugerencias personalizadas según la época y vuestros intereses.</p>
        <Link to="/contacto" className="mt-8 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Escríbenos</Link>
      </section>
    </>
  );
}